import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'

interface RoleProtectedRouteProps {
  children: React.ReactNode
  allowedRoles: string[]
  redirectTo?: string
}

const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({
  children,
  allowedRoles,
  redirectTo = '/dashboard'
}) => {
  const { user } = useAuth()
  const location = useLocation()

  if (!user) {
    // Carry the intended path so login can return the user here (e.g. when
    // following the membership e-card QR on a device without a session).
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} />
  }

  // Temp-credentialed accounts (bulk-created / admin-issued password) must set
  // their own password before using any protected route.
  if (user.mustChangePassword && location.pathname !== '/first-login') {
    return <Navigate to="/first-login" replace />
  }

  if (!allowedRoles.includes(user.role)) {
    // Redirect providers to provider dashboard, others to family dashboard
    const providerRoles = ['PROVIDER', 'AMBULANCE_PROVIDER', 'USTADZ', 'CEMETERY_PROVIDER', 'VENDOR']
    if (providerRoles.includes(user.role)) {
      return <Navigate to="/provider-dashboard" />
    }
    // Finance only has the VA Balance workspace; the default /dashboard target
    // is not visible to FINANCE and would bounce here forever.
    if (user.role === 'FINANCE') {
      return <Navigate to="/finance" replace />
    }
    return <Navigate to={redirectTo} />
  }

  return <>{children}</>
}

export default RoleProtectedRoute